import React from "react";
import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import BorderBottomCard from "../../BorderBottomCard";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import {
  getAccountProfilePath,
  safeFormatTimeAgo,
  sanitizeCompanyLogoUrl,
} from "@/features/dashboard/utils/accountsDashboardUtils";
import { getCleanName } from "@/shared/utils/accountConfig";

const DormantCard = ({ account, onEmail }) => {
  const navigate = useNavigate();
  const path = getAccountProfilePath(account);
  const name = account?.company_name ?? "—";
  const accountStatus = String(account?.account_status || "DORMANT");
  const statusLabel = accountStatus === "REACTIVATION_OPPORTUNITY"
    ? "Reactivation"
    : "Dormant";
  const statusVariant = accountStatus === "REACTIVATION_OPPORTUNITY"
    ? "warning"
    : "danger";
  const inactiveDays = Number(account?.days_since_last_meaningful_engagement);

  const activitySuffix = account?.last_meaningful_engagement_at
    ? safeFormatTimeAgo(account.last_meaningful_engagement_at)
    : "—";

  return (
    <BorderBottomCard
      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors"
      role={path ? "button" : undefined}
      tabIndex={path ? 0 : undefined}
      onClick={() => path && navigate(path)}
      onKeyDown={(e) => {
        if (path && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          navigate(path);
        }
      }}
    >
      <div className="flex gap-x-2 items-start w-full">
        <CommonAvatar
          imageUrl={sanitizeCompanyLogoUrl(account?.company_logo)}
          name={getCleanName(name)}
          className="rounded-full object-cover min-w-8 min-h-8 w-8 h-8 shrink-0"
          size="md"
        />

        <div className="flex flex-col w-full min-w-0">
          <div className="flex w-full items-center justify-between gap-x-2 min-w-0">
            <h5 className="text-gray-800 dark:text-gray-100 text-[0.75rem] leading-5 font-medium truncate">
              {getCleanName(name)}
            </h5>
            <StatusChip
              label={statusLabel}
              variant={statusVariant}
              className="!px-1.5 !py-0 text-[0.6rem] shrink-0"
            />
          </div>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 text-gray-500 dark:text-gray-400 text-[0.69rem] leading-tight font-normal">
            <CustomCommonTooltip
              content={`Last meaningful engagement: ${activitySuffix}`}
              placement="top"
            >
              <span
                className="tabular-nums font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap shrink-0 cursor-help"
                aria-label="Last meaningful engagement"
              >
                {Number.isFinite(inactiveDays) ? `${inactiveDays}d inactive` : "—"}
              </span>
            </CustomCommonTooltip>
          </div>
        </div>
      </div>

      <div
        className="mt-2 flex justify-end"
        onClick={(event) => event.stopPropagation()}
      >
        <SecondaryButton
          title="Re-engage"
          icon={<Mail size={13} />}
          onClick={() => onEmail?.(account)}
          disabled={!onEmail}
          className="!px-2 !py-1 text-[0.65rem]"
        />
      </div>
    </BorderBottomCard>
  );
};

export default DormantCard;
